import type { GoalState } from '@reados/contracts';
import { desktopGoalStatusAction } from '../vendor/codex-desktop/status-action.js';

export type GoalSetInput = { objective: string; tokenBudget?: number };
export type GoalStatusAction = 'active' | 'paused';

/** Composer admission only. The host clears its draft/mode after this resolves. */
export async function submitGoalFromComposer(draft: string, onSet: (input: GoalSetInput) => void | Promise<void>): Promise<boolean> {
  const objective = draft.trim();
  if (!objective) return false;
  await onSet({ objective });
  return true;
}

/** Desktop kQe labels translated; unknown upstream states remain visible. */
export const goalStatusLabels: Record<string, string> = {
  active: '进行中的目标', paused: '已暂停的目标', blocked: '目标受阻',
  usageLimited: '使用额度受限', budgetLimited: '目标预算已用尽', complete: '目标已完成',
};

export function canonicalGoalStatus(status: string): string {
  return ({ budget_limited: 'budgetLimited', usage_limited: 'usageLimited' } as Record<string, string>)[status] ?? status;
}

export function goalStatusAction(status: string): GoalStatusAction | null {
  return desktopGoalStatusAction(canonicalGoalStatus(status)) ?? null;
}

const nonnegative = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value >= 0;
export const isTokenBudget = (value: unknown): value is number =>
  typeof value === 'number' && Number.isSafeInteger(value) && value > 0;

export function formatTokens(value: number, compact = false): string {
  return new Intl.NumberFormat('zh-CN', compact ? { notation: 'compact', maximumFractionDigits: 1 } : { maximumFractionDigits: 0 }).format(value);
}

export function formatDuration(seconds: number): string {
  const total = Math.max(0, Math.floor(seconds));
  const h = Math.floor(total / 3600), m = Math.floor(total % 3600 / 60), s = total % 60;
  return [h ? `${h}h` : '', m ? `${m}m` : '', s || (!h && !m) ? `${s}s` : ''].filter(Boolean).join(' ');
}

/** The IQe display clock only; it does not schedule work or change goal state. */
export function goalSummary(goal: GoalState, nowMs: number = Date.now()) {
  const status = canonicalGoalStatus(goal.status);
  const used = nonnegative(goal.tokensUsed) ? goal.tokensUsed : null;
  const budget = isTokenBudget(goal.tokenBudget) ? goal.tokenBudget : null;
  const elapsed = nonnegative(goal.timeUsedSeconds)
    ? goal.timeUsedSeconds + (status === 'active' && nonnegative(goal.updatedAt)
      ? Math.max(0, (nowMs - goal.updatedAt * 1000) / 1000) : 0)
    : null;
  const tokenProgress = budget === null ? null : `${used === null ? '—' : formatTokens(used, true)} / ${formatTokens(budget, true)}`;
  const elapsedText = elapsed === null ? '耗时待同步' : formatDuration(elapsed);
  return {
    status, label: goalStatusLabels[status] ?? `未知目标状态（${goal.status}）`,
    action: goalStatusAction(status), used, budget, elapsed,
    // Same selection as the desktop IQe: paused/completed states show duration.
    compact: (status === 'active' || status === 'budgetLimited') ? tokenProgress ?? elapsedText : elapsedText,
    elapsedText, remaining: budget !== null && used !== null ? Math.max(0, budget - used) : null,
    overBudget: budget !== null && used !== null ? Math.max(0, used - budget) : null,
    percentage: budget !== null && used !== null ? Math.min(100, used / budget * 100) : null,
  };
}

export function parseGoalDraft(objective: string, budget: string, existing?: GoalState | null):
  { input: GoalSetInput; error?: never } | { error: string; input?: never } {
  const text = objective.trim(), amount = budget.trim();
  if (!text) return { error: '请填写希望完成的目标。' };
  if (!amount) {
    if (isTokenBudget(existing?.tokenBudget)) return { error: '已有预算不能留空。如需取消预算，请清除目标后重新设置。' };
    return { input: { objective: text } };
  }
  const value = Number(amount);
  if (!/^\d+$/.test(amount) || !isTokenBudget(value)) return { error: 'Token 预算须为大于 0 的安全整数。' };
  return { input: { objective: text, tokenBudget: value } };
}
